import * as React from 'react'

import Alert from '@mui/material/Alert'
import Button from '@mui/material/Button'
import Typography from '@mui/material/Typography'

import { LightTooltip } from '@sales-center/components'

import type { DraftLead } from '@user/store/draftLeads/typings'

import { useIsAutoReviewBlocked } from './hooks/useIsAutoReviewBlocked'
import Box from '@mui/material/Box'

interface Props {
  lead: DraftLead
  onOpen: () => void
}

/** Warning strip above a pending lead card that could not be auto-reviewed. */
export function PendingLeadAutoReviewBanner(props: Props) {
  const { lead, onOpen } = props
  const { blocked, actionDisabled, disabledReason } =
    useIsAutoReviewBlocked(lead)

  if (!blocked) return null

  const button = (
    <Button
      size="small"
      color="inherit"
      disabled={actionDisabled}
      onClick={onOpen}
      data-test-id={`pending-lead-auto-review-banner-${lead.id}`}
      sx={{ fontWeight: '500', width: 'max-content', color: '#005aa3' }}
    >
      Update Required Fields
    </Button>
  )

  return (
    <Box pl="16px">
      <Alert
        severity="warning"
        sx={{
          border: 'solid 1px #f0c988',
          borderBottom: '0',
          borderTopLeftRadius: 8,
          borderTopRightRadius: 8,
          borderBottomRightRadius: 0,
          borderBottomLeftRadius: 0,
          alignItems: 'center',
          py: 0,
          '& .MuiAlert-message': {
            p: 0,
          },
          '& .MuiAlert-icon': {
            fontSize: 15,
            mr: '6px',
          },
          width: 'fit-content',
        }}
      >
        <Typography variant="body2">
          This pending lead could not be auto-reviewed because it is missing
          required information.
          {actionDisabled && disabledReason ? (
            <LightTooltip title={disabledReason}>
              <span>{button}</span>
            </LightTooltip>
          ) : (
            button
          )}
        </Typography>
      </Alert>
    </Box>
  )
}

PendingLeadAutoReviewBanner.displayName = 'PendingLeadAutoReviewBanner'
